import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Recipient, Type as DocumentType } from '@components/SendDocuments';
import isEmpty from 'lodash/isEmpty';
import { Props } from '@templates/order/NewOrderTemplate';
import { RecipientType } from '@app/generators/recipientGenerator';
import { CreateOrderBuyer, CreateOrderSeller } from '@apis/models';
import { NewOrderBuyerInfo, NewOrderData, NewOrderSellerInfo } from '@helpers/new-order-loader';

export type NewOrderSendDocumentsHook = {
  recipients: Recipient[];
  setRecipients: Dispatch<SetStateAction<Recipient[]>>;
  allowedTypes: DocumentType[];
  getLatestRecipients(): Recipient[];
};

const AGENT_RECIPIENT_TYPES = [
  RecipientType.CooperatingAgent,
  RecipientType.CooperatingAgentInvited,
  RecipientType.Myself,
  RecipientType.InitiatingAgent,
] as const;

const INVOICE_RECIPIENT_TYPES = [
  RecipientType.ClosingAgent,
  RecipientType.Myself,
  RecipientType.InitiatingAgent,
  RecipientType.CooperatingAgent,
  RecipientType.CooperatingAgentInvited,
] as const;

export const useNewOrderSendDocuments = (
  props: Pick<Props, 'values' | 'userDetails'>,
  getBuyer: (req: NewOrderData) => Partial<CreateOrderBuyer>,
  getSeller: (req: NewOrderData) => Partial<CreateOrderSeller>,
  customCooperatingAgentDetails: {
    agentFirstName?: string;
    agentLastName?: string;
    agentEmail?: string;
  },
  isSendDocumentsVisible: boolean,
): NewOrderSendDocumentsHook => {
  const [recipients, setRecipients] = useState<Recipient[]>([]);

  const isBuyerPay: boolean = props.values.buyerInfo?.buyerPay?.isBuyerPay || false;
  const hasClosingDate = !isEmpty(props.values.closingInfo?.projectedClosingDate);

  const allowedTypes: DocumentType[] = useMemo(
    () => [DocumentType.Confirmation, hasClosingDate ? DocumentType.Invoice : null].filter(Boolean),
    [hasClosingDate],
  );

  const getCooperatingAgent = (req: NewOrderData): NewOrderSellerInfo | NewOrderBuyerInfo => {
    if (req.agentInfo?.representing === 'Buyer' && req.sellerInfo?.AgentEmail) {
      return req.sellerInfo;
    }
    if (req.agentInfo?.representing === 'Seller' && req.buyerInfo?.AgentEmail) {
      return req.buyerInfo;
    }
    return {};
  };

  const applyDefaultSelections = (recipient: Recipient): Recipient => {
    let selectedTypes = [...recipient.selectedTypes];

    if (AGENT_RECIPIENT_TYPES.includes(recipient.type as (typeof AGENT_RECIPIENT_TYPES)[number])) {
      if (!selectedTypes.includes(DocumentType.Confirmation)) {
        selectedTypes.push(DocumentType.Confirmation);
      }
    }

    if (
      hasClosingDate &&
      INVOICE_RECIPIENT_TYPES.includes(recipient.type as (typeof INVOICE_RECIPIENT_TYPES)[number])
    ) {
      if (!isBuyerPay && !selectedTypes.includes(DocumentType.Invoice)) {
        selectedTypes.push(DocumentType.Invoice);
      } else if (isBuyerPay && selectedTypes.includes(DocumentType.Invoice)) {
        selectedTypes = selectedTypes.filter(element => element !== DocumentType.Invoice);
      }
    }

    return { ...recipient, selectedTypes };
  };

  const buildRecipientList = (): Recipient[] => {
    let buyerInfo: Partial<CreateOrderBuyer> = {};
    let sellerInfo: Partial<CreateOrderSeller> = {};
    const cooperatingAgent = getCooperatingAgent(props.values);
    const { agentInfo, closingInfo } = props.values;

    if (!isEmpty(props.values?.buyerInfo?.buyerInfo)) {
      buyerInfo = getBuyer(props.values) || {};
    }
    if (!isEmpty(props.values?.sellerInfo?.sellerInfo)) {
      sellerInfo = getSeller(props.values) || {};
    }

    const potentialRecipients: Recipient[] = [
      {
        name: `${buyerInfo.first_name || ''} ${buyerInfo.last_name || ''}`.trim(),
        email: buyerInfo.email || '',
        type: RecipientType.Buyer,
        selectedTypes: [],
      },
      {
        name: buyerInfo?.co_buyer
          ? `${buyerInfo.co_buyer[0]?.first_name || ''} ${buyerInfo.co_buyer[0]?.last_name || ''}`.trim()
          : '',
        email: buyerInfo?.co_buyer?.[0]?.email || '',
        type: RecipientType.CoBuyer,
        selectedTypes: [],
      },
      {
        name: `${sellerInfo.first_name || ''} ${sellerInfo.last_name || ''}`.trim(),
        email: sellerInfo.email || '',
        type: RecipientType.Seller,
        selectedTypes: [],
      },
      {
        name: sellerInfo?.co_seller
          ? `${sellerInfo.co_seller[0]?.first_name || ''} ${sellerInfo.co_seller[0]?.last_name || ''}`.trim()
          : '',
        email: sellerInfo?.co_seller?.[0]?.email || '',
        type: RecipientType.CoSeller,
        selectedTypes: [],
      },
      {
        name: `${props.userDetails?.firstName || ''} ${props.userDetails?.lastName || ''}`.trim(),
        email: props.userDetails?.email || '',
        type: RecipientType.Myself,
        selectedTypes: [],
      },
      {
        name: agentInfo?.AgentName || '',
        email: agentInfo?.AgentEmail || '',
        type: RecipientType.InitiatingAgent,
        selectedTypes: [],
      },
      {
        name: cooperatingAgent?.AgentName || '',
        email: cooperatingAgent?.AgentEmail || '',
        type: RecipientType.CooperatingAgent,
        selectedTypes: [],
      },
      {
        name: `${customCooperatingAgentDetails?.agentFirstName || ''} ${customCooperatingAgentDetails?.agentLastName || ''}`.trim(),
        email: customCooperatingAgentDetails?.agentEmail || '',
        type: RecipientType.CooperatingAgentInvited,
        selectedTypes: [],
      },
      {
        name: closingInfo?.AgentName || '',
        email: closingInfo?.AgentEmail || '',
        type: RecipientType.ClosingAgent,
        selectedTypes: [],
      },
    ];

    return potentialRecipients
      .filter((r) => !isEmpty(r.email))
      .map((r) => applyDefaultSelections(r));
  };

  const mergeRecipients = (current: Recipient[], updated: Recipient[]): Recipient[] => {
    const merged = [...updated];
    const updatedTypes = new Set(updated.map((r) => r.type));

    current.forEach((existingRecipient) => {
      if (existingRecipient.type === RecipientType.Other) {
        merged.push(existingRecipient);
      } else if (updatedTypes.has(existingRecipient.type)) {
        const updatedRecipient = merged.find((r) => r.type === existingRecipient.type);
        if (updatedRecipient) {
          updatedRecipient.selectedTypes = [...existingRecipient.selectedTypes];
        }
      }
    });

    // Ensure there is one Other recipient by default
    if (!merged.some((r) => r.type === RecipientType.Other)) {
      merged.push({
        name: '',
        email: '',
        type: RecipientType.Other,
        selectedTypes: [],
      });
    }

    return merged;
  };

  const getLatestRecipients = (): Recipient[] => {
    return mergeRecipients(recipients, buildRecipientList());
  };

  useEffect(() => {
    if (!isSendDocumentsVisible) return;

    const latestRecipients = getLatestRecipients();
    setRecipients(latestRecipients);
  }, [
    props.values?.buyerInfo?.email,
    props.values?.buyerInfo?.firstName,
    props.values?.buyerInfo?.lastName,
    props.values?.buyerInfo?.co_buyer,
    props.values?.sellerInfo?.email,
    props.values?.sellerInfo?.firstName,
    props.values?.sellerInfo?.lastName,
    props.values?.sellerInfo?.co_seller,
    props.values?.agentInfo?.AgentEmail,
    props.values?.agentInfo?.AgentName,
    props.userDetails?.email,
    props.userDetails?.firstName,
    props.userDetails?.lastName,
    props.values?.closingInfo?.projectedClosingDate,
    props.values?.closingInfo?.AgentEmail,
    props.values?.closingInfo?.AgentName,
    customCooperatingAgentDetails?.agentEmail,
    customCooperatingAgentDetails?.agentFirstName,
    customCooperatingAgentDetails?.agentLastName,
    isSendDocumentsVisible,
  ]);

  //reset to defaults if buyerPay changes
  useEffect(() => {
    if (!isSendDocumentsVisible) return;
    setRecipients(buildRecipientList());
  }, [
    isBuyerPay,
  ]);

  return {
    recipients,
    setRecipients,
    allowedTypes,
    getLatestRecipients,
  };
};
